<template>
	<div class="bg-ihh-white">
		<div>
			<div v-if="disableCheckout" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.prevent></div>
			<div ref="checkoutBanner" class="bg-ihh-purple py-9 w-full justify-center">
				<div class="flex justify-center">
					<a href="/" class="max-w-[225px] block">
						<CheckoutLogo class="w-full h-auto" />
					</a>
				</div>
			</div>

			<div v-if="loading || loadingStore" class="flex flex-col flex-1 min-h-[calc(100vh-theme(spacing.9)*2-theme(spacing.9))] w-full">
				<Spinner />
			</div>

			<LoadingOverlay :loading="checkout.showOverlay" :data="checkout.overlayData" :message="checkout.overlayData.message" />

			<div id="checkoutData" v-show="!loading" class="flex w-full justify-center md:px-4">
				<div class="w-full flex justify-center md2:justify-around mb-12 px-6 lg:px-0 max-w-6xl 2xl:space-x-16">
					<div class="flex flex-1 lg:max-w-xl xl:max-w-2xl justify-between mt-12">
						<!-- Checkout Details -->
						<div class="w-full max-w-xl mx-auto pb-20">
							<div v-if="!loading" class="w-full">
								<!-- Shipping and Billing Details -->
								<ShippingAndBilling :isDigitalOnly="isDigitalOnly" v-if="cartReady" />

								<!-- Payment Details -->
								<CheckoutDetails title="Payment">
									<template v-slot:button>
										<button
											@click="showPaymentDetails = !showPaymentDetails"
											v-html="showPaymentDetails ? 'Hide' : 'Edit'"
											class="w-24 hover:bg-ihh-gray/10 group-hover:border-ihh-midnight-gray/50 text-center text-sm text-ihh-midnight-gray border rounded-sm border-ihh-gray/50 py-1.5"
										></button>
									</template>

									<template v-slot:details v-if="showPaymentDetails">
										<div class="space-y-4 my-4">
											<template v-if="checkout.expressCheckoutValidated.value">
												<DigitalWallets />
											</template>
											<PaymentTable
												:isDigitalOnly="isDigitalOnly"
												:paymentNotRequired="paymentNotRequired"
												@placingOrder="handlePlacingOrder"
												@jump-to-dw="jumpToDW"
											/>
										</div>
									</template>
								</CheckoutDetails>
							</div>
						</div>
					</div>

					<OrderSummary :isDigitalOnly="isDigitalOnly" />
				</div>
			</div>

			<!-- Cart Validation Modal -->
			<CartValidation v-model:showCartValidationModal="showCartValidationModal" :oosItems="oosItems" :lowStockItems="lowStockItems" />
		</div>
	</div>
</template>

<script>
// Components
import ShippingAndBilling from './shipping-and-billing.vue'
import CheckoutDetails from '../pages/ihh-items/checkout-details.vue'
import CheckoutLogo from '../partials/checkout-logo.vue'
import PaymentTable from './payment-table.vue'
import OrderSummary from './order-summary.vue'
import DigitalWallets from './digital-wallets.vue'
import LoadingOverlay from '../layout/loading-overlay.vue'
import CartValidation from '../layout/cart-validation.vue'

// Helpers
import helpers from '../../helpers/checkout.js'
import * as Sentry from '@sentry/vue'
import { validateInventory } from '../../helpers/validateCart.js'

export default {
	name: 'Checkout',
	components: {
		ShippingAndBilling,
		CheckoutLogo,
		CheckoutDetails,
		PaymentTable,
		OrderSummary,
		DigitalWallets,
		LoadingOverlay,
		CartValidation,
	},
	inject: ['store', 'checkout'],
	data() {
		return {
			// UI Variables
			loading: false,
			loadingStore: true,
			disableCheckout: false,
			cartReady: false,
			showPaymentDetails: false,
			oosItems: [],
			lowStockItems: [],
			showCartValidationModal: false,
		}
	},

	async mounted() {
		console.log('Cart has items: ', this.cartHasItems)
		if (!this.cartHasItems) {
			this.$router.push('/')
			return
		}

		// Validate cart items' inventory
		const { oosItems, lowStockItems } = await validateInventory()
		this.oosItems = oosItems
		this.lowStockItems = lowStockItems

		this.loadingStore = false

		// If cartItems has length and bcCartId is undefined/falsy, reinit the bcCart
		this.checkout.updateShowOverlay(false)
		this.store.showBag = false
		this.loading = true

		this.checkout.resetShippingAddress()
		this.checkout.resetBillingAddress()

		if (this.store?.cart?.bcId) {
			const checkout = await helpers.fetchCheckoutTotals() // fetch BC checkout object

			// if (checkout?.id) {
			// 	if (!this.isDigitalOnly) {
			// 		// if not digital only, verify shipping
			// 		const consignment = checkout?.consignments?.[0]
			// 		console.log('Consignment: ', consignment)

			// 		if (!consignment?.selected_shipping_option?.id) {
			// 			this.checkout.resetShippingAddress() // if no shipping option selected, reset shipping
			// 			this.checkout.resetBillingAddress()
			// 		} else if (consignment?.selected_shipping_option?.id !== this.checkout.shipping.selectedShipping?.id) {
			// 			this.checkout.resetShippingAddress() // if BC selected shipping doesn't match local selected shipping, reset
			// 			this.checkout.resetBillingAddress()
			// 		}
			// 	}
			// } else {
			// 	// TODO: add logic to handle undefined checkout/bc cart id
			// 	console.log(`Checkout ID: ${checkout?.id} is undefined; logging to Sentry`)
			// 	this.logToSentry(checkout)
			// }

			if (!checkout?.id) {
				console.log(`Error fetching checkout totals for bc checkout: ${checkout?.id}; recreating bc Cart`)
				await this.store.saveCart()

				if (!this.store.bcId) {
					this.logToSentry(checkout, `Checkout: Error Fetching Checkout Totals; Error Recreating BC Cart`)
				} else {
					console.log('BC cart recreated: ', this.store.bcId)
				}
			}
		} else {
			// TODO: add logic to handle undefined checkout/bc cart id
			// Attempt to recreate the BC cart with contents of Vue cart
			// If BC cart creation fails, log out the user and attempt again.

			// console.log('store.cart.bcId is undefined; unable to fetch shipping methods')
			console.log('store.cart.bcId is undefined; attempting to recreate BC cart')
			const createCart = await this.store.saveCart()
			console.log('createCart: ', createCart)

			if (!this.store.bcId) {
				this.logToSentry('this.store?.cart?.bcId is undefined; unable to recreate BC cart', 'Checkout: Error Recreating BC Cart')
			} else {
				console.log('BC cart recreated: ', this.store.bcId)
			}
		}
		this.cartReady = true

		this.loading = false

		if (this.oosItems.length > 0 || this.lowStockItems.length > 0) {
			this.showCartValidationModal = true
		}
	},

	methods: {
		jumpToDW() {
			// Wait for next tick to ensure DOM is updated
			this.$nextTick(() => {
				if (this.$refs.checkoutBanner) {
					this.$refs.checkoutBanner.scrollIntoView({ behavior: 'smooth' })
				}
			})
		},
		handlePlacingOrder(value) {
			console.log('handlePlacingOrder: ', value)
			console.log('this.loading before update: ', this.loading)

			this.loading = value
			console.log('this.loading after update: ', this.loading)
		},
		logToSentry(response, message) {
			// if development, skip logging
			if (process.env.NODE_ENV === 'development') {
				return
			}
			Sentry.withScope((scope) => {
				scope.setTag('mangoCart', this.store?.cart?.id || 'mangoCart not found')
				scope.setTag('guest', this.store?.user?.id ? 'false' : 'true')
				scope.setTag('User', this.store?.user?.email || this.checkout?.guest?.email || 'User not found')
				scope.setTag('bcCart', this.store?.cart?.bcId || 'bcCart not found')
				scope.setTag('component', 'Checkout')
				scope.setExtra('shippingAddress', this.checkout.shipping?.shippingAddress || 'shippingAddress is empty')
				scope.setExtra('billingAddress', this.checkout.billing?.billingAddress || 'billingAddress is empty')
				scope.setExtra('selectedShipping', this.checkout.shipping?.selectedShipping || 'selectedShipping is empty')
				scope.setExtra('cartItemsCount', this.store?.cartTotalItems || typeof this.store?.cartTotalItems)
				scope.setExtra('action', 'loadingCheckout')
				scope.setExtra('response', response)
				scope.setLevel('warning')
				Sentry.captureMessage(message)
			})
		},
	},

	computed: {
		isDigitalOnly() {
			return !this.store?.cart?.cartItems?.length && !!this.store?.cart?.certificates?.length
		},

		paymentNotRequired() {
			return this.checkout.displayTotal <= 0
		},
		hideWallets() {
			if ((this.checkout.giftCertificateAmount > 0 || this.checkout.storeCredit > 0) && this.checkout.remainingBalance - this.checkout.shippingTotal <= 0) {
				return true
			}
			return false
		},
		emailValidated() {
			return this.checkout.emailValidated
		},
		cartHasItems() {
			if (!this.store?.cartTotalItems) {
				return false
			}

			return true
		},
	},

	watch: {
		// 'store.cartTotalItems': {
		// 	handler(newVal) {
		// 		console.log('Cart total items: ', this.store.cartTotalItems)
		// 		if (this.store.cartTotalItems === undefined) {
		// 			return
		// 		}

		// 		if (this.store.cartTotalItems < 1) {
		// 			this.$router.push('/')
		// 		}

		// 		if (this.store.cartTotalItems > 0) {
		// 			this.loadingStore = false
		// 			console.log('Cart total items: setting loading to false')
		// 			console.log('Cart total items: loading: ', this.loadingStore)
		// 			return
		// 		}
		// 	},
		// 	immediate: true,
		// },
		'checkout.expressCheckoutValidated.value': {
			handler(newVal) {
				if (newVal) {
					this.showPaymentDetails = true
				}
			},
			immediate: true,
		},
	},
}
</script>

<style scoped></style>
